'use client';

import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import 'css.escape';
import { forwardRef, useId } from 'react';
import styled, { keyframes } from 'styled-components';
import { colorBrandBase, colorBrandLight } from '@nib-components/theme';
const sizeScales = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'];
const sizes = {
  xs: 16,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  xxl: 96,
  xxxl: 128
};
const getSize = size => {
  if (typeof size === 'number') {
    return size;
  }
  if (typeof size === 'string' && Object.prototype.hasOwnProperty.call(sizes, size)) {
    return sizes[size];
  }
  throw new Error(`Size must be one of ${sizeScales.join(', ')} or a valid number. Found ${size} instead`);
};
const iconSpin = keyframes`
  from {
    rotate: 0deg;
  }
  to {
    rotate: 360deg;
  }
`;
const SpinnerIcon = styled.svg`
  animation: ${iconSpin} 1s infinite linear;
`;
// SVG does not support conic gradients. We can leverage a foreignObject and
// clip path to render a conic gradient instead.
const ConicGradient = styled.div`
  width: 100%;
  height: 100%;
  background: conic-gradient(from 180deg, var(--loaderColorBorderA, ${colorBrandBase}), var(--loaderColorBorderB, ${colorBrandLight}));
`;
const smallPathData = 'M12 2C6.477 2 2 6.477 2 12c0 4.065 2.426 7.566 5.913 9.13a1 1 0 0 1-.818 1.825C2.915 21.08 0 16.882 0 12 0 5.373 5.373 0 12 0s12 5.373 12 12c0 4.882-2.915 9.08-7.095 10.955a1 1 0 0 1-.818-1.825A10 10 0 0 0 22 12c0-5.523-4.477-10-10-10';
const largePathData = 'M12 1.5C6.201 1.5 1.5 6.201 1.5 12c0 4.269 2.548 7.945 6.209 9.586a.75.75 0 1 1-.614 1.369C2.915 21.08 0 16.88 0 12 0 5.373 5.373 0 12 0s12 5.373 12 12c0 4.881-2.915 9.08-7.095 10.955a.75.75 0 1 1-.614-1.369A10.5 10.5 0 0 0 22.5 12c0-5.799-4.701-10.5-10.5-10.5';
const smallPathThreshold = 32;
export const Loader = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    size = 24,
    fill,
    description = 'Loading',
    ...svgProps
  } = _ref;
  const clipPathId = useId();
  const loaderSize = getSize(size);
  const path = /*#__PURE__*/_jsx("path", {
    fill: fill,
    fillRule: "evenodd",
    d: loaderSize <= smallPathThreshold ? smallPathData : largePathData,
    clipRule: "evenodd"
  });
  return /*#__PURE__*/_jsxs(SpinnerIcon, {
    ref: ref,
    "data-mesh-component": "LOADER",
    xmlns: "http://www.w3.org/2000/svg",
    width: loaderSize,
    height: loaderSize,
    fill: "none",
    viewBox: "0 0 24 24",
    ...svgProps,
    children: [/*#__PURE__*/_jsx("title", {
      children: description
    }), fill ? path : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("clipPath", {
        id: clipPathId,
        children: path
      }), /*#__PURE__*/_jsx("foreignObject", {
        x: "0",
        y: "0",
        width: "24",
        height: "24",
        clipPath: `url(#${CSS.escape(clipPathId)})`,
        children: /*#__PURE__*/_jsx(ConicGradient, {})
      })]
    })]
  });
});
Loader.displayName = 'Loader';
export default Loader;